import type { StateCreator } from "zustand"
import { create } from "zustand"
import { subscribeWithSelector } from "zustand/middleware"

import { CountryListDocument } from "@/graphql/codegen/graphql"
import type {
  Country,
  CountryListFailure,
  CountryListQuery,
  CountryListQueryVariables,
} from "@/graphql/codegen/graphql"
import { queryClient } from "@/providers/GraphqlRouterProvider"

interface CountriesStore {
  countries: Country[]
  fetching: boolean
  error: string | null
  getCountries: () => Promise<Country[]>
}

const store: StateCreator<CountriesStore> = (set, get) => ({
  countries: [],
  fetching: false,
  error: null,
  getCountries: async () => {
    if (!get().fetching) {
      set({ fetching: true })
      try {
        const { data, error } = await queryClient
          .query<CountryListQuery, CountryListQueryVariables>(
            CountryListDocument,
            { input: { limit: 250 } },
            { requestPolicy: "network-only" } // Force network request
          )
          .toPromise()

        if (error || data?.country.list.__typename === "CountryListFailure") {
          const message = error?.message || (data?.country.list as CountryListFailure).error.message
          set({ error: message, fetching: false })
        } else if (data?.country.list.__typename === "CountryListSuccess") {
          set({
            countries: data?.country.list.countries,
            fetching: false,
            error: null,
          })
        } else {
          set({ countries: [], fetching: false, error: "Failed to fetch countries" })
        }
      } catch (error) {
        set({ error: (error as Error).message, fetching: false })
      }
    }
    return get().countries
  },
})

export const useCountriesStore = create(subscribeWithSelector<CountriesStore>((set, get, val) => store(set, get, val)))
